import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createApiClient } from '../lib/apiClient';
import { notifySuccess } from "../lib/notifications";
import { useUserId } from "../UserIdContext";
import {
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiX,
  FiBox,
  FiDownload,
  FiMaximize2,
  FiMinimize2,
} from "react-icons/fi";
import * as XLSX from "xlsx";
import "./usuarios.css";

/** ========= AXIOS ========= */
const api = createApiClient({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3220",
  headers: { "Content-Type": "application/json" },
});

/** ========= ENDPOINTS ========= */
const API = {
  LISTAR_CLIENTES: "/api/clientes/listar",
  BUSCAR_CLIENTE: "/api/clientes/buscar",
  PALLETS: "/api/pallets",
  PALLETS_OPCIONES: "/api/pallets/opciones",
};

/** ========= BRAND ========= */
const BRAND = "#2BB673"; // verde principal (disponible)
const BRAND_DARK = "#22965c"; // borde verde

const WARN_YELLOW = "#f5d90a"; // amarillo cifra reservada
const WARN_BG = "rgba(245,217,10,.22)"; // fondo amarillo tarjeta
const WARN_BORDER = "rgba(245,217,10,.65)"; // borde amarillo

const DANGER_RED = "#e5484d"; // rojo egresados
const DANGER_BORDER = "#c03438";
const ROTA_RED = "#7a1f24";
const ROTA_BORDER = "rgba(122,31,36,.45)";
const ROTA_BG = "rgba(122,31,36,.15)";
const ROTA_TEXT = "#f6cbd0";

/** ========= STOCK FILTER ========= */
const STOCK_FILTERS = {
  TODOS: "",
  DISPONIBLES: "disponibles", // disponible > 0 && reservado === 0
  RESERVADOS: "reservados", // disponible > 0 && reservado > 0
  EGRESADOS: "egresados", // disponible === 0
};

const CLIENT_FILTER_DEFS = [
  {
    id: "ubicacion",
    label: "Ubicacion",
    aliases: ["Ubicacion", "Ubicación"],
    section: "primary",
  },
  {
    id: "planta",
    label: "Planta",
    aliases: ["Planta"],
    section: "advanced",
    group: "producto",
    type: "text",
    placeholder: "Planta o procedencia",
  },
  {
    id: "unidadNegocio",
    label: "Unidad de negocio",
    aliases: ["Unidad de negocio"],
    section: "advanced",
    group: "producto",
    type: "text",
    placeholder: "Unidad de negocio",
  },
  {
    id: "lote",
    label: "Lote",
    aliases: ["Lote"],
    section: "primary",
  },
  {
    id: "codigo",
    label: "Codigo",
    aliases: ["Codigo", "Código"],
    section: "advanced",
    group: "producto",
    type: "text",
    placeholder: "Codigo de producto",
  },
  {
    id: "detalle",
    label: "Detalle de producto / Nro FC",
    aliases: ["Detalle de producto / N° FC", "Detalle de producto / Nro FC"],
    section: "advanced",
    group: "producto",
    type: "text",
    placeholder: "Detalle, nombre o Nro FC",
  },
  {
    id: "fechaIngreso",
    label: "Fecha ingreso",
    aliases: ["Fecha ingreso"],
    section: "primary",
  },
  {
    id: "fechaElab",
    label: "Fecha elaboracion",
    aliases: ["Fecha elaboracion", "Fecha elaboración"],
    section: "advanced",
    group: "fechas",
    type: "date-range",
  },
  {
    id: "fechaVto",
    label: "Fecha vencimiento",
    aliases: ["Fecha vencimiento"],
    section: "advanced",
    group: "fechas",
    type: "date-range",
  },
  {
    id: "tipoEmbalaje",
    label: "Tipo de embalaje",
    aliases: ["Tipo de embalaje"],
    section: "advanced",
    group: "control",
    type: "text",
    placeholder: "Tipo de embalaje",
  },
  {
    id: "senasa",
    label: "Precinto SENASA",
    aliases: ["Precinto SENASA"],
    section: "advanced",
    group: "control",
    type: "text",
    placeholder: "Precinto o codigo SENASA",
  },
  {
    id: "condicion",
    label: "Condicion",
    aliases: ["Condicion", "Condición"],
    section: "advanced",
    group: "control",
    type: "select",
    options: [
      { value: "", label: "Todas" },
      { value: "OK", label: "OK" },
      { value: "ROTAS", label: "Rotas" },
    ],
  },
  {
    id: "observaciones",
    label: "Observaciones",
    aliases: ["Observaciones"],
    section: "advanced",
    group: "control",
    type: "text",
    placeholder: "Observaciones del pallet",
  },
  {
    id: "unidadesPallet",
    label: "Unidades por pallet",
    aliases: ["Unidades por pallet"],
    section: "advanced",
    group: "control",
    type: "text",
    placeholder: "Cantidad o unidades",
  },
  {
    id: "pesoMedidas",
    label: "Peso / Medidas",
    aliases: ["Peso / Medidas"],
    section: "advanced",
    group: "control",
    type: "text",
    placeholder: "Peso o medidas",
  },
];

const ADVANCED_FILTER_GROUPS = [
  { id: "producto", label: "Producto y origen" },
  { id: "fechas", label: "Fechas complementarias" },
  { id: "control", label: "Control y estado" },
];

const INITIAL_CLIENT_TABLE_FILTERS = {
  planta: "",
  unidadNegocio: "",
  codigo: "",
  detalle: "",
  fechaElabDesde: "",
  fechaElabHasta: "",
  fechaVtoDesde: "",
  fechaVtoHasta: "",
  tipoEmbalaje: "",
  senasa: "",
  condicion: "",
  observaciones: "",
  unidadesPallet: "",
  pesoMedidas: "",
};

const SORT_OPTION_PAIRS = [
  { value: "-fechaIngreso", label: "Fecha ingreso desc" },
  { value: "fechaIngreso", label: "Fecha ingreso asc" },
  { value: "pallet", label: "Nro pallet asc" },
  { value: "-pallet", label: "Nro pallet desc" },
  { value: "id", label: "Identificacion asc" },
  { value: "-id", label: "Identificacion desc" },
  { value: "lote", label: "Lote asc" },
  { value: "-lote", label: "Lote desc" },
  { value: "ubicacion", label: "Ubicacion asc" },
  { value: "-ubicacion", label: "Ubicacion desc" },
  { value: "procedencia", label: "Planta asc" },
  { value: "-procedencia", label: "Planta desc" },
  { value: "unidadNegocio", label: "Unidad de negocio asc" },
  { value: "-unidadNegocio", label: "Unidad de negocio desc" },
  { value: "codigo", label: "Codigo asc" },
  { value: "-codigo", label: "Codigo desc" },
  { value: "detalle", label: "Detalle asc" },
  { value: "-detalle", label: "Detalle desc" },
  { value: "fechaElab", label: "Fecha elaboracion asc" },
  { value: "-fechaElab", label: "Fecha elaboracion desc" },
  { value: "fechaVto", label: "Fecha vencimiento asc" },
  { value: "-fechaVto", label: "Fecha vencimiento desc" },
  { value: "tipoEmbalaje", label: "Tipo de embalaje asc" },
  { value: "-tipoEmbalaje", label: "Tipo de embalaje desc" },
  { value: "senasa", label: "SENASA asc" },
  { value: "-senasa", label: "SENASA desc" },
  { value: "cierre", label: "Condicion asc" },
  { value: "-cierre", label: "Condicion desc" },
  { value: "unidadesPallet", label: "Unidades por pallet asc" },
  { value: "-unidadesPallet", label: "Unidades por pallet desc" },
  { value: "pesoMedidas", label: "Peso / Medidas asc" },
  { value: "-pesoMedidas", label: "Peso / Medidas desc" },
];

const SERVER_SORT_MAP = {
  fechaIngreso: "fechaIngreso",
  "-fechaIngreso": "-fechaIngreso",
  pallet: "nPallet",
  "-pallet": "-nPallet",
  id: "identificacion",
  "-id": "-identificacion",
  lote: "loteDoc.nroLote",
  "-lote": "-loteDoc.nroLote",
  ubicacion: "ubicacion",
  "-ubicacion": "-ubicacion",
  procedencia: "procedenciaPlanta",
  "-procedencia": "-procedenciaPlanta",
  unidadNegocio: "unidadNegocioTexto",
  "-unidadNegocio": "-unidadNegocioTexto",
  codigo: "codigo",
  "-codigo": "-codigo",
  detalle: "detalleProductoTexto",
  "-detalle": "-detalleProductoTexto",
  fechaElab: "fechaElaboracion",
  "-fechaElab": "-fechaElaboracion",
  fechaVto: "fechaVencimiento",
  "-fechaVto": "-fechaVencimiento",
  tipoEmbalaje: "tipoEmbalaje",
  "-tipoEmbalaje": "-tipoEmbalaje",
  senasa: "senasaCodigo",
  "-senasa": "-senasaCodigo",
  cierre: "condicion",
  "-cierre": "-condicion",
  unidadesPallet: "unidadesPorPallet",
  "-unidadesPallet": "-unidadesPorPallet",
  pesoMedidas: "pesoMedidas",
  "-pesoMedidas": "-pesoMedidas",
};

const normalizeComparableText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const normalizeDigits = (value) => String(value ?? "").replace(/\D/g, "");

const formatClienteLabel = (cliente) => {
  if (!cliente) return "";
  const base =
    cliente?.nombreCliente || cliente?.razonSocial || cliente?.codCliente || cliente?._id || "";
  const extras = [];
  if (cliente?.codCliente) extras.push(`Codigo ${cliente.codCliente}`);
  return [base, extras.join(" | ")].filter(Boolean).join(" - ");
};

const resolveServerSort = (sort) => SERVER_SORT_MAP[sort] || "-fechaIngreso";

const matchesText = (value, query) => {
  const normalizedQuery = normalizeComparableText(query);
  if (!normalizedQuery) return true;
  return normalizeComparableText(value).includes(normalizedQuery);
};

const matchesDateRange = (value, from, to) => {
  if (!from && !to) return true;
  if (!value) return false;
  if (from && value < from) return false;
  if (to && value > to) return false;
  return true;
};

const compareSortableValues = (left, right) => {
  const aEmpty = left === "" || left === null || left === undefined;
  const bEmpty = right === "" || right === null || right === undefined;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return String(left).localeCompare(String(right), "es", {
    numeric: true,
    sensitivity: "base",
  });
};

const RESULT_TABLE_COLUMNS = [
  { key: "remito", label: "Nro Remito", width: 120, minWidth: 90 },
  { key: "ubicacion", label: "Ubicacion", width: 100, minWidth: 80 },
  { key: "cliente", label: "Cliente", width: 150, minWidth: 110 },
  { key: "fechaIngreso", label: "Fecha ingreso", width: 110, minWidth: 95 },
  { key: "procedencia", label: "Procedencia / Planta", width: 145, minWidth: 110 },
  { key: "unidadNegocio", label: "Unidad de negocio", width: 145, minWidth: 110 },
  { key: "compIng", label: "Comp Ing", width: 120, minWidth: 95 },
  { key: "fcGrupoProveedor", label: "FC / Grupo / Proveedor", width: 165, minWidth: 130 },
  { key: "fechaElab", label: "F. elab.", width: 95, minWidth: 80 },
  { key: "fechaVto", label: "F. vto", width: 95, minWidth: 80 },
  { key: "codigo", label: "Codigo", width: 115, minWidth: 90 },
  { key: "detalle", label: "Detalle / Nro FC", width: 220, minWidth: 150 },
  { key: "lote", label: "Nro lote", width: 150, minWidth: 120, maxWidth: 260 },
  { key: "id", label: "ID", width: 120, minWidth: 90 },
  { key: "pallet", label: "Nro pallet", width: 105, minWidth: 85 },
  { key: "tipoEmbalaje", label: "Tipo embalaje", width: 120, minWidth: 100 },
  { key: "senasa", label: "Senasa", width: 105, minWidth: 85 },
  { key: "unidadesPallet", label: "Unid. x pallet", width: 105, minWidth: 85 },
  { key: "pesoMedidas", label: "Peso / medidas", width: 135, minWidth: 110 },
  { key: "cantPorFc", label: "Cant x FC", width: 95, minWidth: 80 },
  { key: "daniosIn", label: "Danios In", width: 90, minWidth: 75 },
  { key: "motivoDanioIn", label: "Motivo danio in", width: 150, minWidth: 110 },
  { key: "obsIn", label: "Obs in", width: 170, minWidth: 120 },
  { key: "daniosOut", label: "Danio out", width: 90, minWidth: 75 },
  { key: "motivoDanioOut", label: "Motivo danio out", width: 150, minWidth: 110 },
  { key: "cierre", label: "Cierre", width: 110, minWidth: 90 },
  { key: "fechaEgreso", label: "Fecha egreso", width: 110, minWidth: 95 },
  { key: "estadia", label: "Estadia", width: 85, minWidth: 70 },
  { key: "compEgreso", label: "Comp egreso", width: 120, minWidth: 95 },
  { key: "obsOut", label: "Obs out", width: 170, minWidth: 120 },
];

const AUTO_COLUMN_MAX_WIDTH = 360;
const AUTO_COLUMN_HORIZONTAL_SPACE = 26;
const AUTO_COLUMN_CHARACTER_WIDTH = 7.2;

const estimateResultColumnWidth = (column, rowModels) => {
  const values = (Array.isArray(rowModels) ? rowModels : []).map((model) =>
    String(model?.rowValues?.[column.key] ?? "")
  );
  const longestLength = [String(column.label || ""), ...values].reduce(
    (max, value) => Math.max(max, value.length),
    0
  );
  const estimatedWidth = Math.ceil(
    longestLength * AUTO_COLUMN_CHARACTER_WIDTH + AUTO_COLUMN_HORIZONTAL_SPACE
  );

  return Math.max(
    column.minWidth || 70,
    column.width || 0,
    Math.min(column.maxWidth || AUTO_COLUMN_MAX_WIDTH, estimatedWidth)
  );
};

export default function PalletsPanel() {
  const { deposito } = useUserId();
  const depositoActual = deposito || "binder1";

  /** ====== Estado filtros ====== */
  const [clienteId, setClienteId] = useState("");
  const [qCliente, setQCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [clientesBase, setClientesBase] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [clienteDropdownOpen, setClienteDropdownOpen] = useState(false);

  const [nroLote, setNroLote] = useState("");
  const [remito, setRemito] = useState("");
  const [ubicacion, setUbicacion] = useState(""); // '', 'nacional', 'fiscal'
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [q, setQ] = useState("");

  const [sort, setSort] = useState("-fechaIngreso");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  // Filtro de stock
  const [stockFilter, setStockFilter] = useState(STOCK_FILTERS.TODOS);

  /** ====== Datos ====== */
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  /** ====== Export ====== */
  const [exporting, setExporting] = useState(false);
  const clienteBlurTimerRef = useRef(null);
  const topScrollRef = useRef(null);
  const topScrollInnerRef = useRef(null);
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [showTopScroll, setShowTopScroll] = useState(false);
  const [resultadosPantallaCompleta, setResultadosPantallaCompleta] = useState(false);
  const [resultColWidths, setResultColWidths] = useState(() =>
    RESULT_TABLE_COLUMNS.map((column) => column.width)
  );
  /** ====== Opciones (autocomplete) ====== */
  const [optsRemitos, setOptsRemitos] = useState([]);
  const [optsLotes, setOptsLotes] = useState([]);
  const [clientTableFilters, setClientTableFilters] = useState(
    INITIAL_CLIENT_TABLE_FILTERS
  );

  /** ====== Clientes ====== */
  const normalizeClientes = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.clientes)) return payload.clientes;
    if (Array.isArray(payload?.data?.clientes)) return payload.data.clientes;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    return [];
  };

  const fetchClientes = useCallback(async () => {
    try {
      const { data } = await api.get(API.LISTAR_CLIENTES);
      const list = normalizeClientes(data);
      setClientes(list);
      setClientesBase(list);
    } catch {
      setClientes([]);
      setClientesBase([]);
    }
  }, []);
  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  // Búsqueda cliente server-side (debounce)
  useEffect(() => {
    const qtxt = qCliente.trim();
    const digits = normalizeDigits(qtxt);
    const currentSelectedCliente =
      clientesBase.find((c) => c._id === clienteId) ||
      clientes.find((c) => c._id === clienteId) ||
      null;
    const currentSelectedLabel = formatClienteLabel(currentSelectedCliente);
    if (
      currentSelectedCliente &&
      normalizeComparableText(qtxt) === normalizeComparableText(currentSelectedLabel)
    ) {
      setBuscandoCliente(false);
      return undefined;
    }
    if (qtxt.length < 2 && digits.length < 5) {
      setClientes(clientesBase);
      setBuscandoCliente(false);
      return undefined;
    }
    const t = setTimeout(async () => {
      setBuscandoCliente(true);
      try {
        const { data } = await api.get(API.BUSCAR_CLIENTE, {
          params: { q: qtxt },
        });
        setClientes(normalizeClientes(data));
      } finally {
        setBuscandoCliente(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [clienteId, clientes, clientesBase, qCliente]);

  const clientesFiltrados = useMemo(() => {
    const queryText = normalizeComparableText(qCliente);
    const digits = normalizeDigits(qCliente);
    if (!queryText && !digits) return clientes;

    return clientes.filter((cliente) => {
      const candidateValues = [
        cliente?.nombreCliente,
        cliente?.razonSocial,
        cliente?.nombreFantasia,
        cliente?.email,
        cliente?.codCliente,
      ];

      const matchesQuery =
        !queryText ||
        candidateValues.some((value) => matchesText(value, queryText)) ||
        matchesText(cliente?.CUIT, queryText);
      const matchesDigits = !digits || normalizeDigits(cliente?.CUIT).includes(digits);

      return matchesQuery && matchesDigits;
    });
  }, [clientes, qCliente]);

  const selectedCliente = useMemo(
    () =>
      clientesBase.find((c) => c._id === clienteId) ||
      clientes.find((c) => c._id === clienteId) ||
      null,
    [clienteId, clientes, clientesBase]
  );
  const selectedClienteLabel = useMemo(
    () => formatClienteLabel(selectedCliente),
    [selectedCliente]
  );
  const clienteSeleccionadoCoincide = useMemo(
    () =>
      Boolean(selectedCliente?._id) &&
      normalizeComparableText(qCliente) ===
        normalizeComparableText(selectedClienteLabel),
    [qCliente, selectedCliente, selectedClienteLabel]
  );
  const clientesSugeridos = useMemo(() => {
    const queryCode = normalizeComparableText(qCliente);
    if (!queryCode) return clientesFiltrados;

    const exactCodeMatches = clientesFiltrados.filter(
      (cliente) => normalizeComparableText(cliente?.codCliente) === queryCode
    );

    return exactCodeMatches.length > 0 ? exactCodeMatches : clientesFiltrados;
  }, [clientesFiltrados, qCliente]);
  const clearClienteBlurTimer = useCallback(() => {
    if (clienteBlurTimerRef.current) {
      clearTimeout(clienteBlurTimerRef.current);
      clienteBlurTimerRef.current = null;
    }
  }, []);
  const handleSelectCliente = useCallback(
    (cliente) => {
      clearClienteBlurTimer();
      setClienteId(cliente?._id || "");
      setQCliente(formatClienteLabel(cliente));
      setClienteDropdownOpen(false);
    },
    [clearClienteBlurTimer]
  );
  const handleClienteInputChange = useCallback(
    (event) => {
      const nextValue = event.target.value;
      setQCliente(nextValue);
      setClienteDropdownOpen(Boolean(nextValue.trim()));

      if (
        clienteId &&
        normalizeComparableText(nextValue) !==
          normalizeComparableText(selectedClienteLabel)
      ) {
        setClienteId("");
      }
    },
    [clienteId, selectedClienteLabel]
  );
  const handleClienteFocus = useCallback(() => {
    clearClienteBlurTimer();
    setClienteDropdownOpen(true);
  }, [clearClienteBlurTimer]);
  const handleClienteBlur = useCallback(() => {
    clearClienteBlurTimer();
    clienteBlurTimerRef.current = setTimeout(() => {
      setClienteDropdownOpen(false);
      clienteBlurTimerRef.current = null;
    }, 120);
  }, [clearClienteBlurTimer]);
  const handleClienteKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        setClienteDropdownOpen(false);
        return;
      }
      if (event.key !== "Enter") return;
      if (buscandoCliente || clientesSugeridos.length !== 1) return;

      event.preventDefault();
      handleSelectCliente(clientesSugeridos[0]);
    },
    [buscandoCliente, clientesSugeridos, handleSelectCliente]
  );
  const filtrosCliente = useMemo(
    () =>
      Array.isArray(selectedCliente?.Filtros)
        ? selectedCliente.Filtros
        : [],
    [selectedCliente]
  );
  const tieneFiltrosCliente = filtrosCliente.length > 0;
  const filtrosClienteSet = useMemo(
    () => new Set(filtrosCliente.map((filtro) => normalizeComparableText(filtro))),
    [filtrosCliente]
  );
  const hasClientFilter = useCallback(
    (...aliases) => {
      if (!tieneFiltrosCliente) return true;
      return aliases.some((alias) => {
        const normalizedAlias = normalizeComparableText(alias);
        if (filtrosClienteSet.has(normalizedAlias)) return true;
        if (normalizedAlias.includes("remito")) {
          return filtrosCliente.some((filtro) =>
            normalizeComparableText(filtro).includes("remito")
          );
        }
        return false;
      });
    },
    [filtrosCliente, filtrosClienteSet, tieneFiltrosCliente]
  );
  const isRemitoVisible = true;
  const isLoteVisible = useMemo(() => hasClientFilter("Lote"), [hasClientFilter]);
  const isUbicacionVisible = useMemo(() => hasClientFilter("Ubicacion"), [hasClientFilter]);
  const isFechaVisible = useMemo(() => hasClientFilter("Fecha ingreso"), [hasClientFilter]);
  const isBusquedaLibreVisible = !tieneFiltrosCliente;
  const configuredClientFilterDefs = useMemo(
    () =>
      CLIENT_FILTER_DEFS.filter((filterDef) =>
        filterDef.aliases.some((alias) =>
          filtrosClienteSet.has(normalizeComparableText(alias))
        )
      ),
    [filtrosClienteSet]
  );
  const visibleClientFilterDefs = useMemo(
    () =>
      tieneFiltrosCliente ? configuredClientFilterDefs : CLIENT_FILTER_DEFS,
    [configuredClientFilterDefs, tieneFiltrosCliente]
  );
  const visibleAdvancedClientFilters = useMemo(
    () =>
      visibleClientFilterDefs.filter((filterDef) => filterDef.section === "advanced"),
    [visibleClientFilterDefs]
  );
  const advancedFilterGroups = useMemo(() => {
    const grouped = ADVANCED_FILTER_GROUPS.map((group) => ({ ...group, filters: [] }));
    const groupedMap = new Map(grouped.map((group) => [group.id, group]));

    visibleAdvancedClientFilters.forEach((filterDef) => {
      const groupId = filterDef.group || "control";
      const targetGroup = groupedMap.get(groupId);

      if (targetGroup) {
        targetGroup.filters.push(filterDef);
        return;
      }

      grouped.push({
        id: groupId,
        label: filterDef.label,
        filters: [filterDef],
      });
    });

    return grouped.filter((group) => group.filters.length > 0);
  }, [visibleAdvancedClientFilters]);
  const sortOptions = useMemo(() => {
    const visibleKeys = new Set(["fechaIngreso", "pallet", "id"]);

    visibleClientFilterDefs.forEach((filterDef) => {
      if (filterDef.id === "planta") visibleKeys.add("procedencia");
      else if (filterDef.id === "condicion") visibleKeys.add("cierre");
      else visibleKeys.add(filterDef.id);
    });

    return SORT_OPTION_PAIRS.filter((option) =>
      visibleKeys.has(option.value.replace(/^-/, ""))
    );
  }, [visibleClientFilterDefs]);

  useEffect(() => {
    if (sortOptions.some((option) => option.value === sort)) return;
    setSort(sortOptions[0]?.value || "-fechaIngreso");
  }, [sort, sortOptions]);

  useEffect(() => {
    if (clienteId) return;

    const qtxt = qCliente.trim();
    const digits = normalizeDigits(qtxt);
    if (!qtxt || buscandoCliente) return;
    if (qtxt.length < 2 && digits.length < 5) return;
    if (clientesSugeridos.length !== 1) return;

    handleSelectCliente(clientesSugeridos[0]);
  }, [buscandoCliente, clienteId, clientesSugeridos, handleSelectCliente, qCliente]);

  useEffect(() => () => clearClienteBlurTimer(), [clearClienteBlurTimer]);

  useEffect(() => {
    setClientTableFilters(INITIAL_CLIENT_TABLE_FILTERS);
  }, [clienteId]);

  /** ====== Opciones (remitos/lotes) ====== */
  const fetchOpciones = useCallback(async () => {
    try {
      const { data } = await api.get(API.PALLETS_OPCIONES, {
        params: { clienteId: clienteId || undefined, deposito: depositoActual },
      });
      setOptsRemitos(data?.data?.remitos || []);
      setOptsLotes(data?.data?.lotes || []);
    } catch {
      setOptsRemitos([]);
      setOptsLotes([]);
    }
  }, [clienteId, depositoActual]);
  useEffect(() => {
    fetchOpciones();
  }, [fetchOpciones]);

  /** ====== Listado ====== */
  const fetchPallets = async () => {
    setLoading(true);
    /* harcodeo el id del cliente porque no tengo los endpoints de cliente
    setClienteId("6902e639d3b09d11d66f1de2");*/
    try {
      const params = {
        clienteId: clienteId || undefined,
        deposito: depositoActual,
        page,
        limit,
        sort: resolveServerSort(sort),
      };
      if (isRemitoVisible && remito) params.remito = remito;
      if (isLoteVisible && nroLote) params.nroLote = nroLote;
      if (isUbicacionVisible && ubicacion) {
        params.ubicacion = ubicacion;
      }
      if (isFechaVisible && desde) params.desde = desde;
      if (isFechaVisible && hasta) params.hasta = hasta;
      if (isBusquedaLibreVisible && q) params.q = q;

      const { data } = await api.get(API.PALLETS, { params });
      const b = data?.data || {};
      setItems(b.items || []);
      setTotal(b.total || 0);
      setPages(b.pages || 1);
    } catch (err) {
      console.error("GET /api/pallets", err);
      setItems([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Auto-filtrar al cambiar los filtros (debounce suave)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchPallets();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    limit,
    sort,
    depositoActual,
    clienteId,
    remito,
    nroLote,
    ubicacion,
    desde,
    hasta,
    q,
  ]);

  // Si cambian filtros “fuertes”, volvemos a página 1
  useEffect(() => {
    setPage(1);
  }, [clienteId, remito, nroLote, ubicacion, desde, hasta, q]);

  const resetFiltros = () => {
    setClienteId("");
    setQCliente("");
    setNroLote("");
    setRemito("");
    setUbicacion("");
    setDesde("");
    setHasta("");
    setQ("");
    setSort("-fechaIngreso");
    setPage(1);
    setLimit(50);
    setStockFilter(STOCK_FILTERS.TODOS);
    setClientTableFilters(INITIAL_CLIENT_TABLE_FILTERS);
  };

  /** ====== Helpers ====== */
  const fmtDMY = (v) => {
    if (!v) return "";
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "";
      const [y, m, dd] = d.toISOString().slice(0, 10).split("-");
      return `${dd}/${m}/${y}`;
    } catch {
      return "";
    }
  };

  const excelCellValue = (value) => {
    if (value == null) return "*";
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : "*";
    }
    if (typeof value === "string") {
      return value.trim() ? value : "*";
    }
    return value;
  };

  const computeEstadia = useCallback((ing, egr) => {
    if (!ing || !egr) return "";
    const a = new Date(ing).setHours(0, 0, 0, 0);
    const b = new Date(egr).setHours(0, 0, 0, 0);
    const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
    return Number.isFinite(diff) ? diff : "";
  }, []);

  // Flags de stock por pallet
  const getStockFlags = useCallback((p) => {
    const dRaw = Number(p?.disponible);
    const disponible = Number.isFinite(dRaw) ? dRaw : NaN;
    const reservado = Number(p?.reservado) || 0;

    const isDisponible =
      Number.isFinite(disponible) && disponible > 0 && reservado === 0;
    const isReservado =
      Number.isFinite(disponible) && disponible > 0 && reservado > 0;
    const isEgresado = Number.isFinite(disponible) && disponible === 0; // definición pedida

    return { isDisponible, isReservado, isEgresado, disponible, reservado };
  }, []);

  const isRotaPallet = (p) => {
    const condicion = p?.condicion ?? p?.ingreso?.condicion ?? "";
    return String(condicion).toLowerCase().includes("rota");
  };

  const buildRowModels = useCallback(
    (sourceItems, clienteRef = selectedCliente) =>
      (Array.isArray(sourceItems) ? sourceItems : []).map((p) => {
        const prod = p?.producto || {};
        const lot = p?.lote || {};
        const ingreso = p?.ingreso || {};
        const danios = p?.danios || {};
        const clienteNombre =
          ingreso?.clienteNombre ||
          ingreso?.cliente?.nombreCliente ||
          clienteRef?.nombreCliente ||
          "";
        const remitoValue =
          ingreso?.nroRemito || ingreso?.remito || p?.nroRemito || p?.remito || "";
        const ubicacionBinder = p?.ubicacion || ingreso?.ubicacionTexto || "";
        const procedencia = p?.procedenciaPlanta || p?.plantaTexto || ingreso?.planta || "";
        const unidadNegocio =
          p?.unidadNegocioTexto ||
          ingreso?.unidadNegocio ||
          prod?.catProducto ||
          "";
        const fcGrupoProveedor =
          p?.fcGrupoProveedor ||
          prod?.detalleProducto ||
          prod?.nombreProducto ||
          "";
        const fechaIngreso = fmtDate(p?.fechaIngreso);
        const fechaElab = fmtDate(lot?.fechaFab || p?.fechaElaboracion);
        const fechaVto = fmtDate(lot?.fechaVto || p?.fechaVencimiento);
        const fechaEgreso = fmtDate(p?.fechaEgreso);
        const pesoMedidas = [prod?.peso, prod?.medidas, p?.pesoMedidas]
          .filter(Boolean)
          .join(" / ");
        const cantPorFc = p?.cantPorFc ?? prod?.cantidadxfc ?? "";
        const unidadesRaw = p?.unidadesPorPallet ?? p?.cantidad ?? "";
        const unidadesPalletNumber = Number(unidadesRaw);
        const unidadesPallet = Number.isFinite(unidadesPalletNumber)
          ? unidadesPalletNumber
          : unidadesRaw;
        const detalle =
          prod?.detalleProducto ||
          prod?.nombreProducto ||
          p?.detalleProductoTexto ||
          p?.nroFc ||
          "";
        const observaciones = [
          p?.observacionesIngreso,
          danios?.obsIn,
          danios?.obsOut,
          prod?.obsGeneral,
        ]
          .filter(Boolean)
          .join(" | ");

        return {
          item: p,
          rowValues: {
            remito: remitoValue,
            ubicacion: ubicacionBinder,
            cliente: clienteNombre,
            fechaIngreso,
            procedencia,
            unidadNegocio,
            compIng: ingreso?.remito || "",
            fcGrupoProveedor,
            fechaElab,
            fechaVto,
            codigo: prod?.codProducto || p?.codigo || "",
            detalle,
            lote: lot?.nroLote || p?.loteTexto || "",
            id: p?.identificacion || p?.idInterno || "",
            pallet: p?.nPallet || "",
            tipoEmbalaje: p?.tipoEmbalaje || "",
            senasa: p?.senasaCodigo || p?.recintoSenasa || "",
            unidadesPallet,
            pesoMedidas,
            cantPorFc,
            daniosIn: danios?.daniosIn ? "Si" : "",
            motivoDanioIn: p?.motivoDanioIn || "",
            obsIn: danios?.obsIn || "",
            daniosOut: danios?.daniosOut ? "Si" : "",
            motivoDanioOut: "",
            cierre: p?.condicion || "",
            fechaEgreso,
            estadia: computeEstadia(p?.fechaIngreso, p?.fechaEgreso),
            compEgreso: "",
            obsOut: danios?.obsOut || "",
            observaciones,
          },
          sortValues: {
            remito: remitoValue,
            ubicacion: ubicacionBinder,
            cliente: clienteNombre,
            fechaIngreso,
            procedencia,
            unidadNegocio,
            compIng: ingreso?.remito || "",
            fcGrupoProveedor,
            fechaElab,
            fechaVto,
            codigo: prod?.codProducto || p?.codigo || "",
            detalle,
            lote: lot?.nroLote || p?.loteTexto || "",
            id: p?.identificacion || p?.idInterno || "",
            pallet: p?.nPallet || "",
            tipoEmbalaje: p?.tipoEmbalaje || "",
            senasa: p?.senasaCodigo || p?.recintoSenasa || "",
            unidadesPallet: Number.isFinite(unidadesPalletNumber)
              ? unidadesPalletNumber
              : String(unidadesRaw || ""),
            pesoMedidas,
            cierre: p?.condicion || "",
            fechaEgreso,
            estadia: computeEstadia(p?.fechaIngreso, p?.fechaEgreso),
            observaciones,
          },
        };
      }),
    [computeEstadia, selectedCliente]
  );

  const matchesClientTableFilters = useCallback(
    (model) => {
      const row = model?.rowValues || {};
      return (
        matchesText(row.procedencia, clientTableFilters.planta) &&
        matchesText(row.unidadNegocio, clientTableFilters.unidadNegocio) &&
        matchesText(row.codigo, clientTableFilters.codigo) &&
        matchesText(row.detalle, clientTableFilters.detalle) &&
        matchesDateRange(
          row.fechaElab,
          clientTableFilters.fechaElabDesde,
          clientTableFilters.fechaElabHasta
        ) &&
        matchesDateRange(
          row.fechaVto,
          clientTableFilters.fechaVtoDesde,
          clientTableFilters.fechaVtoHasta
        ) &&
        matchesText(row.tipoEmbalaje, clientTableFilters.tipoEmbalaje) &&
        matchesText(row.senasa, clientTableFilters.senasa) &&
        matchesText(row.cierre, clientTableFilters.condicion) &&
        matchesText(row.observaciones, clientTableFilters.observaciones) &&
        matchesText(row.unidadesPallet, clientTableFilters.unidadesPallet) &&
        matchesText(row.pesoMedidas, clientTableFilters.pesoMedidas)
      );
    },
    [clientTableFilters]
  );

  const applyClientSideFiltersAndSort = useCallback(
    (models) => {
      const direction = sort.startsWith("-") ? -1 : 1;
      const sortKey = sort.replace(/^-/, "");

      return [...(Array.isArray(models) ? models : [])]
        .filter((model) => {
          const flags = getStockFlags(model?.item);
          if (stockFilter === STOCK_FILTERS.DISPONIBLES && !flags.isDisponible) {
            return false;
          }
          if (stockFilter === STOCK_FILTERS.RESERVADOS && !flags.isReservado) {
            return false;
          }
          if (stockFilter === STOCK_FILTERS.EGRESADOS && !flags.isEgresado) {
            return false;
          }
          return matchesClientTableFilters(model);
        })
        .sort((left, right) => {
          const leftValue =
            left?.sortValues?.[sortKey] ?? left?.rowValues?.[sortKey] ?? "";
          const rightValue =
            right?.sortValues?.[sortKey] ?? right?.rowValues?.[sortKey] ?? "";
          return compareSortableValues(leftValue, rightValue) * direction;
        });
    },
    [getStockFlags, matchesClientTableFilters, sort, stockFilter]
  );

  const rowModels = useMemo(
    () => buildRowModels(items, selectedCliente),
    [buildRowModels, items, selectedCliente]
  );
  const rowModelsFiltrados = useMemo(
    () => applyClientSideFiltersAndSort(rowModels),
    [applyClientSideFiltersAndSort, rowModels]
  );
  const itemsFiltrados = useMemo(
    () => rowModelsFiltrados.map((model) => model.item),
    [rowModelsFiltrados]
  );

  useEffect(() => {
    if (rowModelsFiltrados.length === 0) return;

    setResultColWidths((currentWidths) => {
      const nextWidths = RESULT_TABLE_COLUMNS.map((column, index) =>
        Math.max(
          currentWidths[index] || column.width,
          estimateResultColumnWidth(column, rowModelsFiltrados)
        )
      );
      const unchanged = nextWidths.every(
        (width, index) => width === currentWidths[index]
      );
      return unchanged ? currentWidths : nextWidths;
    });
  }, [rowModelsFiltrados]);

  const resumenStock = useMemo(() => {
    let totalUnidades = 0;
    let totalPallets = itemsFiltrados.length;
    let rotasPallets = 0;
    let rotasUnidades = 0;

    for (const p of itemsFiltrados) {
      const unidadesRaw = p?.unidadesPorPallet ?? p?.cantidad;
      const unidades = Number(unidadesRaw);
      if (Number.isFinite(unidades)) {
        totalUnidades += unidades;
      }
      if (isRotaPallet(p)) {
        rotasPallets += 1;
        if (Number.isFinite(unidades)) {
          rotasUnidades += unidades;
        }
      }
    }

    return { totalUnidades, totalPallets, rotasPallets, rotasUnidades };
  }, [itemsFiltrados]);

  const syncTableScroll = useCallback(() => {
    const top = topScrollRef.current;
    const topInner = topScrollInnerRef.current;
    const bottom = tableScrollRef.current;
    const table = tableRef.current;
    if (!top || !topInner || !bottom || !table) return;

    const width = table.scrollWidth;
    const widthPx = `${width}px`;
    if (topInner.style.width !== widthPx) {
      topInner.style.width = widthPx;
    }

    const shouldShow = width > bottom.clientWidth + 2;
    setShowTopScroll((prev) => (prev === shouldShow ? prev : shouldShow));
  }, []);

  useEffect(() => {
    const top = topScrollRef.current;
    const bottom = tableScrollRef.current;
    if (!top || !bottom) return;

    const handleTopScroll = () => {
      if (bottom.scrollLeft !== top.scrollLeft) {
        bottom.scrollLeft = top.scrollLeft;
      }
    };
    const handleBottomScroll = () => {
      if (top.scrollLeft !== bottom.scrollLeft) {
        top.scrollLeft = bottom.scrollLeft;
      }
    };

    top.addEventListener("scroll", handleTopScroll);
    bottom.addEventListener("scroll", handleBottomScroll);

    return () => {
      top.removeEventListener("scroll", handleTopScroll);
      bottom.removeEventListener("scroll", handleBottomScroll);
    };
  }, []);

  useEffect(() => {
    let rafId;
    const schedule = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncTableScroll);
    };

    schedule();
    window.addEventListener("resize", schedule);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      const table = tableRef.current;
      const bottom = tableScrollRef.current;
      if (table) {
        ro = new ResizeObserver(schedule);
        ro.observe(table);
        if (bottom) ro.observe(bottom);
      }
    }

    return () => {
      window.removeEventListener("resize", schedule);
      if (ro) ro.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [syncTableScroll]);

  useEffect(() => {
    const rafId = requestAnimationFrame(syncTableScroll);
    return () => cancelAnimationFrame(rafId);
  }, [syncTableScroll, itemsFiltrados, resultColWidths, resultadosPantallaCompleta]);

  useEffect(() => {
    if (!resultadosPantallaCompleta) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === "Escape") setResultadosPantallaCompleta(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [resultadosPantallaCompleta]);

  const startResizeColumn = (index) => (event) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const minWidth = RESULT_TABLE_COLUMNS[index]?.minWidth ?? 70;
    const startWidth =
      resultColWidths[index] ||
      event.currentTarget.parentElement.getBoundingClientRect().width;

    const handleMouseMove = (moveEvent) => {
      const nextWidth = Math.max(minWidth, startWidth + moveEvent.clientX - startX);
      setResultColWidths((prev) => {
        const next = [...prev];
        next[index] = nextWidth;
        return next;
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  /** ====== Exportar Excel (respeta filtro stock actual) ====== */
  const exportarExcel = async () => {
    try {
      setExporting(true);

      // Pido "todo" con filtros de servidor (menos el stockFilter, que es cliente)
      const { data } = await api.get(API.PALLETS, {
        params: {
          deposito: depositoActual,
          clienteId: clienteId || undefined,
          remito: isRemitoVisible ? remito || undefined : undefined,
          nroLote: isLoteVisible ? nroLote || undefined : undefined,
          ubicacion: isUbicacionVisible ? ubicacion || undefined : undefined,
          q: isBusquedaLibreVisible ? q || undefined : undefined,
          desde: isFechaVisible ? desde || undefined : undefined,
          hasta: isFechaVisible ? hasta || undefined : undefined,
          page: 1,
          limit: 5000,
          sort: resolveServerSort(sort),
        },
      });
      const rows = applyClientSideFiltersAndSort(
        buildRowModels(data?.data?.items || [], selectedCliente)
      );

      const headers = [
        "UBICACION",
        "CLIENTE",
        "F ING",
        "PROCEDENCIA/PLANTA",
        "UNIDAD DE NEGOCIO",
        "COMP ING",
        "FC/GRUPO/PROVEEDOR",
        "F ELAB",
        "VTO",
        "CODIGO",
        "DETALLE /PROD/NRO FC",
        "LOTE",
        "ID",
        "NRO DE PALLET",
        "TIPO DE EMBALAJE",
        "PREC .SENASA LACTEOS",
        "UN X PALLETS",
        "PESO/MEDIDAS",
        "CANT X FC",
        "DANOS IN",
        "OBS IN",
        "DANOS OUT",
        "MOTIVO DANO",
        "CIERRE",
        "F EGRE",
        "ESTADIA",
        "COMP EGRESO",
        "0",
        "RESERVADO",
        "EGRESADO",
        "DISPONIBLE ACTUAL",
      ];

      const aoa = [headers];
      for (const model of rows) {
        const p = model?.item || {};
        const prod = p?.producto || {};
        const lot = p?.lote || {};
        const row = model?.rowValues || {};

        aoa.push([
          row.ubicacion || "",
          row.cliente || selectedCliente?.nombreCliente || "",
          fmtDMY(p?.fechaIngreso),
          row.procedencia || "",
          // Si ya guardás unidadNegocio en el pallet, usalo; si no, fallback a catProducto:
          row.unidadNegocio || "",
          row.remito || "",
          "*",
          fmtDMY(lot?.fechaFab),
          fmtDMY(lot?.fechaVto),
          row.codigo || "",
          row.detalle || "",
          row.lote || "",
          row.id || "",
          row.pallet || "",
          row.tipoEmbalaje || "",
          row.senasa || p?.precSenasaLacteo || "",
          Number.isFinite(Number(row.unidadesPallet)) ? Number(row.unidadesPallet) : "",
          row.pesoMedidas || [prod?.peso, prod?.medidas].filter(Boolean).join("/"),
          row.cantPorFc || "",
          row.daniosIn ? 1 : 0,
          row.obsIn || "",
          row.daniosOut ? 1 : 0,
          row.obsOut || "",
          row.cierre || "",
          fmtDMY(p?.fechaEgreso),
          computeEstadia(p?.fechaIngreso, p?.fechaEgreso),
          "",
          "",
          Number.isFinite(+p?.reservado) ? +p.reservado : 0,
          Number.isFinite(+p?.egresado) ? +p.egresado : 0,
          Number.isFinite(+p?.disponible) ? +p.disponible : "",
        ].map(excelCellValue));
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const headerStyle = {
        font: { bold: true },
        fill: { patternType: "solid", fgColor: { rgb: "FFE6F4EA" } },
        alignment: { horizontal: "distributed", vertical: "distributed" },
      };
      headers.forEach((_, idx) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: idx });
        if (ws[cellAddress]) {
          ws[cellAddress].s = headerStyle;
        }
      });
      ws["!cols"] = headers.map(() => ({ wch: 18 }));
      ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: aoa.length - 1, c: headers.length - 1 },
        }),
      };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Pallets");

      const nombreCliente =
        selectedCliente?.nombreCliente?.replace(/[\\/:*?"<>|]/g, "") || "Todos";
      const fecha = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Pallets_${nombreCliente}_${fecha}.xlsx`, {
        cellStyles: true,
      });
      notifySuccess("Archivo de pallets generado correctamente.");
    } catch (e) {
      console.error("Exportar Excel", e);
      alert("No pude exportar el Excel. Revisá la consola para más detalles.");
    } finally {
      setExporting(false);
    }
  };

  /** ====== UI ====== */
  const updateClientTableFilter = (key, value) => {
    setClientTableFilters((prev) => ({ ...prev, [key]: value }));
  };
  const showRemito = true;
  const showLote = hasClientFilter("Lote");
  const showUbicacion = hasClientFilter("Ubicacion", "Ubicación");
  const showFecha = hasClientFilter("Fecha ingreso");
  const showBusquedaLibre = !tieneFiltrosCliente;

  const renderAdvancedClientFilter = (filterDef) => {
    if (filterDef.type === "date-range") {
      const fromKey = `${filterDef.id}Desde`;
      const toKey = `${filterDef.id}Hasta`;
      return (
        <div className="field field--dynamic field--dynamic-range" key={filterDef.id}>
          <label>{filterDef.label}</label>
          <div className="date-range-inline">
            <input
              type="date"
              value={clientTableFilters[fromKey] || ""}
              onChange={(e) => updateClientTableFilter(fromKey, e.target.value)}
            />
            <input
              type="date"
              value={clientTableFilters[toKey] || ""}
              onChange={(e) => updateClientTableFilter(toKey, e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (filterDef.type === "select") {
      return (
        <div className="field field--dynamic" key={filterDef.id}>
          <label>{filterDef.label}</label>
          <select
            className="filter-select"
            value={clientTableFilters[filterDef.id] || ""}
            onChange={(e) => updateClientTableFilter(filterDef.id, e.target.value)}
          >
            {filterDef.options.map((option) => (
              <option key={`${filterDef.id}-${option.value || "all"}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="field field--dynamic" key={filterDef.id}>
        <label>{filterDef.label}</label>
        <input
          value={clientTableFilters[filterDef.id] || ""}
          onChange={(e) => updateClientTableFilter(filterDef.id, e.target.value)}
          placeholder={filterDef.placeholder || filterDef.label}
          style={{ textAlign: "center" }}
        />
      </div>
    );
  };

  return (
    <div className="users-wrap pallets-compact">
      {/* Header */}
      <div className="users-header">
        <div className="title-stack">
          <h1 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FiBox aria-hidden="true" /> REPORTADOR DE STOCK
          </h1>
          <p className="subtitle">
            Filtrá por cliente, remito, lote, fechas o búsqueda libre
          </p>
        </div>
        <div className="toolbar pallets-toolbar">
          <button type="button" className="btn ghost" onClick={fetchPallets}>
            <FiRefreshCw /> {loading ? "Actualizando..." : "Actualizar"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={exportarExcel}
            disabled={exporting || itemsFiltrados.length === 0}
            title="Exporta el resultado actual a Excel"
          >
            <FiDownload /> {exporting ? "Exportando..." : "Exportar Excel"}
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="panel pallets-filters" style={{ marginBottom: 12 }}>
        <div className="filters-row filters-row--primary">
          <div className="field field--client">
            <label>
              <FiFilter style={{ marginRight: 6 }} /> Cliente
            </label>
            <div className="cliente-autocomplete">
              <div className="input-search input-search--client">
                <FiSearch />
                <input
                  type="text"
                  value={qCliente}
                  onChange={handleClienteInputChange}
                  onFocus={handleClienteFocus}
                  onBlur={handleClienteBlur}
                  onKeyDown={handleClienteKeyDown}
                  placeholder="Todos, nombre o codigo de cliente"
                  style={{ textAlign: "center" }}
                />
              </div>
              {buscandoCliente &&
                clienteDropdownOpen &&
                !clienteSeleccionadoCoincide && (
                  <div className="cliente-estado">Buscando...</div>
                )}
              {clienteDropdownOpen &&
                !clienteSeleccionadoCoincide &&
                qCliente.trim().length > 0 &&
                clientesSugeridos.length > 0 && (
                  <div className="cliente-list client-dropdown">
                    {clientesSugeridos.map((cliente) => (
                      <div
                        key={cliente._id}
                        className="cliente-item client-dropdown__item"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          handleSelectCliente(cliente);
                        }}
                      >
                        <strong>{cliente.nombreCliente || cliente.codCliente}</strong>
                        {(cliente.codCliente || cliente.CUIT) && (
                          <div className="cliente-sub">
                            {[
                              cliente.codCliente
                                ? `Codigo ${cliente.codCliente}`
                                : null,
                              cliente.CUIT ? `CUIT ${cliente.CUIT}` : null,
                            ]
                              .filter(Boolean)
                              .join(" | ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>
            {selectedCliente && (
              <div className="client-selected-summary">
                <strong>{selectedCliente.nombreCliente || "Cliente seleccionado"}</strong>
                <span>
                  {[
                    selectedCliente.codCliente
                      ? `Codigo ${selectedCliente.codCliente}`
                      : null,
                    selectedCliente.CUIT ? `CUIT ${selectedCliente.CUIT}` : null,
                  ]
                    .filter(Boolean)
                    .join(" | ") || "Filtrando por este cliente"}
                </span>
              </div>
            )}
          </div>

          {showRemito && (
            <div className="field field--remito">
              <label>Remito</label>
              <input
                list="remitosList"
                value={remito}
                onChange={(e) => setRemito(e.target.value)}
                placeholder="Ej: R-000123"
                style={{ textAlign: "center" }}
              />
              <datalist id="remitosList">
                {optsRemitos.map((r, i) => (
                  <option key={i} value={r} />
                ))}
              </datalist>
            </div>
          )}

          {showLote && (
            <div className="field field--lote">
              <label>N° de Lote</label>
              <input
                list="lotesList"
                value={nroLote}
                onChange={(e) => setNroLote(e.target.value)}
                placeholder="Parcial o exacto"
                style={{ textAlign: "center" }}
              />
              <datalist id="lotesList">
                {optsLotes.map((r, i) => (
                  <option key={i} value={r} />
                ))}
              </datalist>
            </div>
          )}

          {showUbicacion && (
            <div className="field field--ubicacion">
              <label>Ubicación</label>
            <select
              className="filter-select"
              value={ubicacion}
              onChange={(e) => setUbicacion(e.target.value)}
              style={{
                background: "var(--panel-strong)",
                color: "var(--text)",
                border: "1px solid var(--stroke)",
                borderRadius: 10,
                padding: "6px 8px",
                fontSize: 12,
                outline: "none",
                textAlign: "center",
              }}
            >
                <option value="">Todas</option>
                <option value="nacional">Nacional</option>
                <option value="fiscal">Fiscal</option>
              </select>
            </div>
          )}

          {showFecha && (
            <>
              <div className="field field--desde">
                <label>Desde</label>
                <input
                  type="date"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                  style={{ textAlign: "center" }}
                />
              </div>
              <div className="field field--hasta">
                <label>Hasta</label>
                <input
                  type="date"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                  style={{ textAlign: "center" }}
                />
              </div>
            </>
          )}

        </div>

        <div className="filters-row filters-row--secondary">

          {showBusquedaLibre && (
            <div className="field field--search">
              <label>Búsqueda libre</label>
              <div className="input-search">
                <FiSearch />
                <input
                  placeholder="Identificacion, Nro pallet, cliente o codigo/nombre de producto..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ textAlign: "center" }}
                />
              </div>
            </div>
          )}

          <div className="field field--sort">
            <label>Orden</label>
            <select
              className="filter-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={{
                background: "var(--panel-strong)",
                color: "var(--text)",
                border: "1px solid var(--stroke)",
                borderRadius: 10,
                padding: "6px 8px",
                fontSize: 12,
                outline: "none",
                textAlign: "center",
              }}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field field--limit">
            <label>Límite</label>
            <input
              type="number"
              min="10"
              step="10"
              value={limit}
              onChange={(e) =>
                setLimit(Math.max(10, parseInt(e.target.value || "50", 10)))
              }
              style={{ textAlign: "center" }}
            />
          </div>

          {/* 🔹 Filtro de stock */}
          <div className="field field--stock">
            <label>Stock</label>
            <select
              className="filter-select"
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              style={{
                background: "var(--panel-strong)",
                color: "var(--text)",
                border: "1px solid var(--stroke)",
                borderRadius: 10,
                padding: "6px 8px",
                fontSize: 12,
                outline: "none",
                textAlign: "center",
              }}
            >
              <option value={STOCK_FILTERS.TODOS}>Todos</option>
              <option value={STOCK_FILTERS.DISPONIBLES}>Disponibles</option>
              <option value={STOCK_FILTERS.RESERVADOS}>Reservados</option>
              <option value={STOCK_FILTERS.EGRESADOS}>Egresados</option>
            </select>
          </div>

          <div className="field field--actions">
            <div className="filter-actions">
              <button className="btn ghost" type="button" onClick={resetFiltros}>
                <FiX /> Limpiar
              </button>
            </div>
          </div>
        </div>
        {advancedFilterGroups.length > 0 && (
          <div className="filters-advanced-layout">
            {advancedFilterGroups.map((group) => (
              <section className="filter-group filter-group--advanced" key={group.id}>
                <p className="filter-group__title">{group.label}</p>
                <div className="filters-row filters-row--client-group">
                  {group.filters.map((filterDef) => renderAdvancedClientFilter(filterDef))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Listado en TARJETAS */}
      <div
        className={`panel results-panel${
          resultadosPantallaCompleta ? " results-panel--fullscreen" : ""
        }`}
        role={resultadosPantallaCompleta ? "dialog" : undefined}
        aria-modal={resultadosPantallaCompleta ? "true" : undefined}
        aria-labelledby="resultados-stock-title"
      >
        <div className="users-header results-header" style={{ marginBottom: 10 }}>
          <div className="title-stack">
            <div className="results-title-row">
              <h2 id="resultados-stock-title" style={{ margin: 0 }}>
                Resultados
              </h2>
              <button
                type="button"
                className="btn ghost results-fullscreen-btn"
                aria-pressed={resultadosPantallaCompleta}
                onClick={() =>
                  setResultadosPantallaCompleta((current) => !current)
                }
              >
                {resultadosPantallaCompleta ? (
                  <FiMinimize2 aria-hidden="true" />
                ) : (
                  <FiMaximize2 aria-hidden="true" />
                )}
                {resultadosPantallaCompleta
                  ? "Cerrar pantalla completa"
                  : "Ver en pantalla completa"}
              </button>
            </div>
            <p className="subtitle">
              {loading
                ? "Cargando..."
                : `${itemsFiltrados.length} registros mostrados (${total} totales)`}
              {!loading && " - Arrastra el borde del encabezado para cambiar el ancho"}
            </p>
          </div>
          <div className="chips" style={{ alignItems: "center" }}>
            <span className="badge">
              Pallets: {resumenStock.totalPallets}
            </span>
            <span className="badge">
              Unidades: {resumenStock.totalUnidades}
            </span>
            <span
              className="badge"
              style={{
                background: ROTA_BG,
                borderColor: ROTA_BORDER,
                color: ROTA_RED,
              }}
            >
              Pallets rotas: {resumenStock.rotasPallets}
            </span>
            <span
              className="badge"
              style={{
                background: ROTA_BG,
                borderColor: ROTA_BORDER,
                color: ROTA_RED,
              }}
            >
              Unidades rotas: {resumenStock.rotasUnidades}
            </span>
          </div>
        </div>

        {itemsFiltrados.length === 0 && !loading && (
          <div
            className="panel"
            style={{ opacity: 0.8, textAlign: "center", padding: 18 }}
          >
            Sin resultados
          </div>
        )}

        <div
          className="results-table-area"
        /*className="list"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
          }}*/
        >
          {/* <ResultPalletCard key={p._id} p={p} /> */}
          {/* listado de pallets version filas y columnas */}
          <div className="table-scroll-wrap">
            <div
              className={`table-scrollbar ${showTopScroll ? "" : "hidden"}`}
              ref={topScrollRef}
              aria-hidden="true"
            >
              <div className="table-scrollbar__inner" ref={topScrollInnerRef} />
            </div>
            <div className="table-responsive" ref={tableScrollRef}>
              <table
                className="table table-sm table-striped mb-0 tabla-pallets"
                ref={tableRef}
              >
              {false && (
                <>
              <thead className="table-light">
                <tr>
                  <th className="col-xs">Nº Remito</th>
                  <th className="col-xs">Ubicacion</th>
                  <th className="col-xs">Cliente</th>
                  <th className="col-xs">Fecha Ingreso</th>
                  <th className="col-xs">Procedencia/ Planta</th>
                  <th className="col-xs">Unidad de Negocio</th>
                  <th className="col-xs">Comp Ing</th>
                  <th className="col-xs">FC/ Grupo/ Proveedor</th>
                  <th className="col-s">F. elab.</th>
                  <th className="col-s">F. vto</th>
                  <th className="col-xs">Código</th>
                  <th className="col-l">Detalle / Nº FC</th>
                  <th className="col-xs">Nº lote</th>
                  <th className="col-xs">ID</th>
                  <th className="col-xs">Nº pallet</th>
                  <th className="col-xs">Tipo embalaje</th>
                  <th className="col-xs">Senasa</th>
                  <th className="col-xs">Unid. x pallet</th>
                  <th className="col-xs">Peso/ medidas</th>
                  <th className="col-xs">Cant x FC</th>
                  <th className="col-xs">Daños In</th>
                  <th className="col-xs">Motivo daño in</th>
                  <th className="col-l">Obs in</th>
                  <th className="col-xs">Daño out</th>
                  <th className="col-xs">Motivo daño out</th>
                  <th className="col-xs">Cierre</th>
                  <th className="col-xs">Fecha Egreso</th>
                  <th className="col-xs">Estadia</th>
                  <th className="col-xs">Comp Egreso</th>
                  <th className="col-l">Obs Out</th>
                </tr>
              </thead>
              <tbody>
                {itemsFiltrados.map((p) => {
                  const prod = p?.producto || {};
                  const lot = p?.lote || {};
                  const ingreso = p?.ingreso || {};
                  const danios = p?.danios || {};
                  const clienteNombre =
                    ingreso?.clienteNombre ||
                    ingreso?.cliente?.nombreCliente ||
                    selectedCliente?.nombreCliente ||
                    "";
                  const remitoValue =
                    ingreso?.nroRemito ||
                    ingreso?.remito ||
                    p?.nroRemito ||
                    p?.remito ||
                    "";
                  const ubicacionBinder = p?.ubicacion || ingreso?.ubicacionTexto || "";
                  const procedencia = p?.procedenciaPlanta || p?.plantaTexto || "";
                  const unidadNegocio = p?.unidadNegocioTexto || ingreso?.unidadNegocio || "";
                  const fcGrupoProveedor =
                    p?.fcGrupoProveedor || prod?.detalleProducto || prod?.nombreProducto || "";
                  const pesoMedidas = [prod?.peso, prod?.medidas, p?.pesoMedidas]
                    .filter(Boolean)
                    .join(" / ");
                  const cantPorFc = p?.cantPorFc ?? prod?.cantidadxfc ?? "";
                  const unidadesPallet = p?.unidadesPorPallet ?? p?.cantidad ?? "";
                  const esRota = isRotaPallet(p);
                  const rotaCellStyle = esRota
                    ? { color: ROTA_TEXT, fontWeight: 700 }
                    : undefined;
                  const rowClassName = esRota ? "text-center row-rota" : "text-center";

                  return (
                    <tr className={rowClassName} key={p._id}>
                      <td>{remitoValue}</td>
                      <td>{ubicacionBinder}</td>
                      <td>{clienteNombre}</td>
                      <td>{fmtDate(p.fechaIngreso)}</td>
                      <td>{procedencia}</td>
                      <td>{unidadNegocio}</td>
                      <td>{ingreso?.remito || ""}</td>
                      <td>{fcGrupoProveedor}</td>
                      <td>{fmtDate(lot?.fechaFab || p?.fechaElaboracion)}</td>
                      <td>{fmtDate(lot?.fechaVto || p?.fechaVencimiento)}</td>
                      <td>{prod?.codProducto || p?.codigo}</td>
                      <td>
                        {prod?.detalleProducto ||
                          prod?.nombreProducto ||
                          p?.detalleProductoTexto ||
                          p?.nroFc}
                      </td>
                      <td>{lot?.nroLote || p?.loteTexto}</td>
                      <td>{p?.identificacion || p?.idInterno}</td>
                      <td style={rotaCellStyle}>{p?.nPallet}</td>
                      <td>{p?.tipoEmbalaje}</td>
                      <td>{p?.senasaCodigo || p?.recintoSenasa}</td>
                      <td style={rotaCellStyle}>{unidadesPallet}</td>
                      <td>{pesoMedidas}</td>
                      <td>{cantPorFc}</td>
                      <td>{danios?.daniosIn ? "Sí" : ""}</td>
                      <td>{p?.motivoDanioIn || ""}</td>
                      <td>{danios?.obsIn || ""}</td>
                      <td>{danios?.daniosOut ? "Sí" : ""}</td>
                      <td>{""}</td>
                      <td>{p?.condicion || ""}</td>
                      <td>{fmtDate(p?.fechaEgreso || "-")}</td>
                      <td>{computeEstadia(p?.fechaIngreso, p?.fechaEgreso)}</td>
                      <td>{""}</td>
                      <td>{danios?.obsOut || ""}</td>
                    </tr>
                  );
                })}
              </tbody>
                </>
              )}
              <colgroup>
                {resultColWidths.map((width, index) => (
                  <col
                    key={RESULT_TABLE_COLUMNS[index]?.key || index}
                    style={{ width: `${width}px` }}
                  />
                ))}
              </colgroup>
              <thead className="table-light">
                <tr>
                  {RESULT_TABLE_COLUMNS.map((column, index) => (
                    <th key={column.key} title={column.label}>
                      <span className="tabla-pallets__th-label">{column.label}</span>
                      <span
                        className="col-resize-handle"
                        onMouseDown={startResizeColumn(index)}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {itemsFiltrados.map((p) => {
                  const prod = p?.producto || {};
                  const lot = p?.lote || {};
                  const ingreso = p?.ingreso || {};
                  const danios = p?.danios || {};
                  const clienteNombre =
                    ingreso?.clienteNombre ||
                    ingreso?.cliente?.nombreCliente ||
                    selectedCliente?.nombreCliente ||
                    "";
                  const remitoValue =
                    ingreso?.nroRemito ||
                    ingreso?.remito ||
                    p?.nroRemito ||
                    p?.remito ||
                    "";
                  const ubicacionBinder = p?.ubicacion || ingreso?.ubicacionTexto || "";
                  const procedencia = p?.procedenciaPlanta || p?.plantaTexto || "";
                  const unidadNegocio =
                    p?.unidadNegocioTexto || ingreso?.unidadNegocio || "";
                  const fcGrupoProveedor =
                    p?.fcGrupoProveedor ||
                    prod?.detalleProducto ||
                    prod?.nombreProducto ||
                    "";
                  const pesoMedidas = [prod?.peso, prod?.medidas, p?.pesoMedidas]
                    .filter(Boolean)
                    .join(" / ");
                  const cantPorFc = p?.cantPorFc ?? prod?.cantidadxfc ?? "";
                  const unidadesPallet = p?.unidadesPorPallet ?? p?.cantidad ?? "";
                  const esRota = isRotaPallet(p);
                  const rotaCellStyle = esRota
                    ? { color: ROTA_TEXT, fontWeight: 700 }
                    : undefined;
                  const rowClassName = esRota ? "text-center row-rota" : "text-center";
                  const rowValues = {
                    remito: remitoValue,
                    ubicacion: ubicacionBinder,
                    cliente: clienteNombre,
                    fechaIngreso: fmtDate(p.fechaIngreso),
                    procedencia,
                    unidadNegocio,
                    compIng: ingreso?.remito || "",
                    fcGrupoProveedor,
                    fechaElab: fmtDate(lot?.fechaFab || p?.fechaElaboracion),
                    fechaVto: fmtDate(lot?.fechaVto || p?.fechaVencimiento),
                    codigo: prod?.codProducto || p?.codigo || "",
                    detalle:
                      prod?.detalleProducto ||
                      prod?.nombreProducto ||
                      p?.detalleProductoTexto ||
                      p?.nroFc ||
                      "",
                    lote: lot?.nroLote || p?.loteTexto || "",
                    id: p?.identificacion || p?.idInterno || "",
                    pallet: p?.nPallet || "",
                    tipoEmbalaje: p?.tipoEmbalaje || "",
                    senasa: p?.senasaCodigo || p?.recintoSenasa || "",
                    unidadesPallet,
                    pesoMedidas,
                    cantPorFc,
                    daniosIn: danios?.daniosIn ? "Si" : "",
                    motivoDanioIn: p?.motivoDanioIn || "",
                    obsIn: danios?.obsIn || "",
                    daniosOut: danios?.daniosOut ? "Si" : "",
                    motivoDanioOut: "",
                    cierre: p?.condicion || "",
                    fechaEgreso: fmtDate(p?.fechaEgreso || "-"),
                    estadia: computeEstadia(p?.fechaIngreso, p?.fechaEgreso),
                    compEgreso: "",
                    obsOut: danios?.obsOut || "",
                  };

                  return (
                    <tr className={rowClassName} key={`${p._id}-resizable`}>
                      {RESULT_TABLE_COLUMNS.map((column) => {
                        const value = rowValues[column.key] ?? "";
                        const cellStyle =
                          column.key === "pallet" || column.key === "unidadesPallet"
                            ? rotaCellStyle
                            : undefined;

                        return (
                          <td key={column.key} title={String(value ?? "")}>
                            <div className="tabla-pallets__cell" style={cellStyle}>
                              {value}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </div>

        {/* Paginación */}
        <div className="pager" style={{ justifyContent: "space-between" }}>
          <div className="subtitle" style={{ opacity: 0.85 }}>
            Página {page} de {pages}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <FiChevronLeft /> Anterior
            </button>
            <button
              className="btn"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}

/* ===================== Tarjeta de resultado ===================== */
function ResultPalletCard({ p }) {
  const [open, setOpen] = useState(false);
  const prod = p?.producto || {};
  const lot = p?.lote || {};

  // Flags de stock (según definición del usuario)
  const disponible = Number.isFinite(+p?.disponible) ? +p.disponible : NaN;
  const reservadoN = Number(p?.reservado) || 0;

  const isDisponible =
    Number.isFinite(disponible) && disponible > 0 && reservadoN === 0;
  const isReservado =
    Number.isFinite(disponible) && disponible > 0 && reservadoN > 0;
  const isEgresado = Number.isFinite(disponible) && disponible === 0;

  // Estilo de tarjeta
  const cardStyle = (() => {
    if (isDisponible) {
      return {
        display: "flex",
        flexDirection: "column",
        gap: 10,
        border: `1px solid ${BRAND_DARK}`,
        boxShadow: "0 8px 24px rgba(43,182,115,.12)",
        backgroundImage:
          "linear-gradient(180deg, rgba(43,182,115,0.08), rgba(43,182,115,0.02))",
        transition:
          "box-shadow .2s ease, border-color .2s ease, background .2s ease",
      };
    }
    if (isReservado) {
      return {
        display: "flex",
        flexDirection: "column",
        gap: 10,
        border: `1px solid ${WARN_BORDER}`,
        boxShadow: "0 8px 24px rgba(245,217,10,.14)",
        backgroundImage:
          "linear-gradient(180deg, rgba(245,217,10,.18), rgba(245,217,10,.08))",
        transition:
          "box-shadow .2s ease, border-color .2s ease, background .2s ease",
      };
    }
    if (isEgresado) {
      return {
        display: "flex",
        flexDirection: "column",
        gap: 10,
        border: `1px solid ${DANGER_BORDER}`,
        boxShadow: "0 8px 24px rgba(229,72,77,.20)",
        backgroundImage:
          "linear-gradient(180deg, rgba(229,72,77,.18), rgba(229,72,77,.08))",
        transition:
          "box-shadow .2s ease, border-color .2s ease, background .2s ease",
      };
    }
    return {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      border: "1px solid var(--stroke)",
      transition:
        "box-shadow .2s ease, border-color .2s ease, background .2s ease",
    };
  })();

  const nroRemito = p?.remito || p?.nroRemito || p?.ingreso?.nroRemito || "—";

  return (
    <div className="panel" style={cardStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              display: "grid",
              placeItems: "center",
              background: BRAND,
              border: `1px solid ${BRAND_DARK}`,
              position: "relative",
              overflow: "hidden",
            }}
            title="Binder"
          >
            <img
              src={process.env.PUBLIC_URL + "/binder-mark-white.png"}
              alt="Binder"
              width={26}
              height={26}
              style={{ objectFit: "contain" }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <FiBox
              size={22}
              style={{ color: "#fff", position: "absolute", opacity: 0.95 }}
            />
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, lineHeight: 1.15 }}>
              {prod?.nombreProducto ||
                prod?.codProducto ||
                "Producto sin nombre"}
            </div>
            <div
              style={{
                fontSize: 12,
                opacity: 0.75,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 360,
              }}
              title={`${p?.identificacion || "—"} · ${p?.nPallet || "—"}`}
            >
              {p?.identificacion || "—"} · N° Pallet {p?.nPallet || "—"}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 6,
            flexShrink: 0,
            alignItems: "center",
          }}
        >
          <span style={{ textTransform: "capitalize" }}>
            {p?.ubicacion || ""}
          </span>

          {isEgresado && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                padding: "4px 8px",
                borderRadius: 999,
                background: "rgba(229,72,77,.15)",
                border: `1px solid ${DANGER_BORDER}`,
                color: DANGER_RED,
              }}
              title="Disponible = 0"
            >
              Egresado
            </span>
          )}

          {isReservado && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                padding: "4px 8px",
                borderRadius: 999,
                background: WARN_BG,
                border: `1px solid ${WARN_BORDER}`,
                color: "#795f00",
              }}
              title="Tiene reservas (disponible > 0 y reservado > 0)"
            >
              Reservado
            </span>
          )}

          {isDisponible && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                padding: "4px 8px",
                borderRadius: 999,
                background: "rgba(43,182,115,.18)",
                border: `1px solid ${BRAND_DARK}`,
                color: BRAND,
              }}
              title="Disponible > 0 y sin reservas"
            >
              Disponible
            </span>
          )}
        </div>
      </div>

      <div
        className="form-grid"
        style={{ gridTemplateColumns: "repeat(2, minmax(0,1fr))" }}
      >
        <FieldView label="N° Remito" value={nroRemito} />
        <FieldView label="Código" value={prod?.codProducto} />
        <FieldView
          label="Cantidad"
          value={`${p?.cantidad ?? 0} ${p?.unidad || ""}`.trim()}
        />
        <FieldView label="Lote" value={lot?.nroLote} />
        <FieldView label="F. Vto" value={fmtDate(lot?.fechaVto)} />
        <FieldView label="Ingreso" value={fmtDate(p?.fechaIngreso)} />
        {Number.isFinite(+p?.disponible) && (
          <FieldView
            label="Disponible"
            value={
              isDisponible || isReservado ? (
                <span style={{ color: BRAND, fontWeight: 800 }}>
                  {p.disponible}
                </span>
              ) : (
                p.disponible
              )
            }
          />
        )}
        <FieldView
          label="Cant. Reservada"
          value={
            <span style={{ color: WARN_YELLOW, fontWeight: 800 }}>
              {reservadoN}
            </span>
          }
        />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="btn" onClick={() => setOpen(true)}>
          <FiEye /> Ver
        </button>
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)}>
          <h3 style={{ marginTop: 0 }}>Detalle pallet</h3>

          <div
            className="form-grid"
            style={{ gridTemplateColumns: "repeat(2, minmax(0,1fr))" }}
          >
            <FieldView label="N° Remito" value={nroRemito} />
            <FieldView label="Identificación" value={p?.identificacion} />
            <FieldView label="N° Pallet" value={p?.nPallet} />
            <FieldView label="Ubicación" value={p?.ubicacion} />
            <FieldView label="Fecha ingreso" value={fmtDate(p?.fechaIngreso)} />
            <FieldView label="Unidad" value={p?.unidad} />
            <FieldView label="Cantidad (actual)" value={p?.cantidad} />
            <FieldView label="SENASA" value={p?.senasaCodigo} />
            {/* Datos adicionales */}
            <FieldView label="Tipo de embalaje" value={p?.tipoEmbalaje} />
            <FieldView
              label="Procedencia/Planta"
              value={p?.procedenciaPlanta}
            />
            <FieldView
              label="PREC. SENASA LÁCTEO"
              value={p?.precSenasaLacteo}
            />
            {/* Stock */}
            <FieldView label="Reservado (backend)" value={reservadoN} />
            {Number.isFinite(+p?.egresado) && (
              <FieldView label="Egresado (acumulado)" value={p.egresado} />
            )}
            {Number.isFinite(+p?.disponible) && (
              <FieldView label="Disponible (backend)" value={p.disponible} />
            )}
          </div>

          <div style={{ height: 8 }} />
          <strong style={{ display: "block", marginBottom: 6 }}>
            Producto
          </strong>
          <div
            className="form-grid"
            style={{ gridTemplateColumns: "repeat(2, minmax(0,1fr))" }}
          >
            <FieldView label="Código" value={p?.producto?.codProducto} />
            <FieldView label="Nombre" value={p?.producto?.nombreProducto} />
            <FieldView label="Detalle" value={p?.producto?.detalleProducto} />
            <FieldView label="Categoría" value={p?.producto?.catProducto} />
            <FieldView label="Unidad máx." value={p?.producto?.unidadMaxima} />
            <FieldView label="Peso" value={p?.producto?.peso} />
            <FieldView label="Medidas" value={p?.producto?.medidas} />
            <FieldView label="Cantidad x FC" value={p?.producto?.cantidadxfc} />
            <FieldView
              label="Obs. General"
              value={p?.producto?.obsGeneral}
              colSpan={2}
            />
          </div>

          <div style={{ height: 8 }} />
          <strong style={{ display: "block", marginBottom: 6 }}>Lote</strong>
          <div
            className="form-grid"
            style={{ gridTemplateColumns: "repeat(2, minmax(0,1fr))" }}
          >
            <FieldView label="N° Lote" value={lot?.nroLote} />
            <FieldView label="Vencimiento" value={fmtDate(lot?.fechaVto)} />
            <FieldView label="Fabricación" value={fmtDate(lot?.fechaFab)} />
          </div>
        </Modal>
      )}
    </div>
  );
}

function FieldView({ label, value, colSpan }) {
  return (
    <div
      className="field"
      style={colSpan ? { gridColumn: `span ${colSpan}` } : undefined}
    >
      <label style={{ opacity: 0.7 }}>{label}</label>
      <div
        style={{
          background: "var(--panel-strong)",
          border: "1px solid var(--stroke)",
          borderRadius: 12,
          padding: "10px 12px",
          color: "var(--text)",
          minHeight: 40,
          display: "flex",
          alignItems: "center",
          wordBreak: "break-word",
        }}
      >
        {value ?? <span style={{ opacity: 0.6 }}>—</span>}
      </div>
    </div>
  );
}

function fmtDate(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

function Modal({ children, onClose, title = "Detalle" }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(0,0,0,.5)",
        display: "grid",
        placeItems: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        className="panel"
        style={{
          width: "min(980px, 95vw)",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,.45)",
          borderRadius: 16,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 18 }}>{title}</div>
          <button className="btn icon" onClick={onClose} title="Cerrar">
            <FiX />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
